import React, { ReactNode, forwardRef } from 'react';

import { css } from '@emotion/core';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { color } from '@styles/themes';
import { Layout as ALayout, LayoutProps } from 'antd';
import { tuple } from 'antd/lib/_util/type';
import { SiderProps } from 'antd/lib/layout';

const { Header: AHeader, Sider: ASider, Content: AContent, Footer: AFooter } = ALayout;

// LAYOUT
export const tYzTypeLayout = tuple(
  'APP',
  'VERTICAL_MIDDLE',
  'BODY_BACKGROUND',
  'B2C_RESULT_BACKGROUND',
  'SURVEY',
  'MBTI_REGISTER_PC',
);
type TYzTypeLayout = (typeof tYzTypeLayout)[number] | undefined;
interface ILayoutParams extends LayoutProps {
  yzTypeLayout?: TYzTypeLayout;
}
type THandleLayoutWithTypeParams = {
  yzTypeLayout?: TYzTypeLayout;
};
const handleLayoutWithType = ({ yzTypeLayout }: THandleLayoutWithTypeParams) => {
  switch (yzTypeLayout) {
    case 'BODY_BACKGROUND':
      return css`
        position: relative;
        padding: 0 24px;
      `;
    case 'B2C_RESULT_BACKGROUND':
      return css`
        padding: 0;
        background: #c2d5eb;
      `;
    case 'APP':
      return css`
        min-height: calc(100vh - 40px - 72px); // 40px(header), 72px(footer)
      `;
    case 'VERTICAL_MIDDLE':
      return css`
        justify-content: center;
      `;
    case 'SURVEY':
      return css`
        transform: translateX(24px);
        padding-right: 24px;
        margin-left: -24px;
        width: calc(100% + 24px);
      `;
    case 'MBTI_REGISTER_PC':
      return css`
        max-width: none;
        max-height: none;
        min-height: 100dvh;
        background: linear-gradient(180deg, #f3f7fc 0%, #ffffff 60%);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px 16px;
      `;
    default:
      return `
      `;
  }
};
const StyledLayoutBase = styled(ALayout, { shouldForwardProp: isPropValid })<ILayoutParams>`
  background: ${color.white};
  height: 100dvh;
  overflow: auto;
  width: 100%;
  max-width: 480px;
  max-height: 1000px;
  ${({ yzTypeLayout }) => handleLayoutWithType({ yzTypeLayout })}
`;

const StyledLayout = forwardRef<HTMLDivElement, ILayoutParams>((props, ref) => (
  <StyledLayoutBase {...props} ref={ref} />
));
export const Layout = forwardRef<HTMLDivElement, ILayoutParams>(({ children, ...rest }, ref) => (
  <StyledLayout {...rest} ref={ref}>
    {children}
  </StyledLayout>
));

// HEADER
interface IHeaderParams extends LayoutProps {}
const StyledHead = styled(AHeader)`
  width: 100%;
  height: initial;
  padding: 0;
`;
export const Header = ({ children, ...rest }: IHeaderParams) => <StyledHead {...rest}>{children}</StyledHead>;

// SIDER
const StyledSider = styled(ASider)`
  overflow: auto;
  background: #fff;
  border-right: 1px solid #f0f0f0;

  .ant-layout-sider-trigger {
    background: ${({ theme }) => theme.main4};
  }
`;
type TSiderParams = {
  children?: ReactNode;
} & Omit<SiderProps, 'theme'>;
export const Sider = ({ children, ...rest }: TSiderParams) => <StyledSider {...rest}>{children}</StyledSider>;

// CONTENT
export const tYzTypeContent = tuple('MAIN', 'BODY', 'TAIL', 'TOP', 'CONTENT');
type TYzTypeContent = (typeof tYzTypeContent)[number] | undefined;
interface IContentParams extends LayoutProps {
  yzTypeContent?: TYzTypeContent;
  selectableRowFlag?: boolean;
}
type THandleContentWithTypeParams = {
  yzTypeContent?: TYzTypeContent;
};
const handleContentWithType = ({ yzTypeContent }: THandleContentWithTypeParams) => {
  switch (yzTypeContent) {
    case 'MAIN':
      return css`
        background: ${color.white};
        width: 100%;
        /* padding: 24px; */
        margin: 0;
      `;
    case 'BODY':
      return css`
        background: ${color.white};
        padding: 30px 36px;
        border: 1px solid rgba(186, 185, 197, 0.47);
        margin-top: 25px;
      `;
    case 'TAIL':
      return css`
        margin-top: 40px;
        margin-bottom: 80px;
        padding-left: 40px;
        padding-right: 40px;
      `;
    case 'TOP':
      return css`
        padding: 24px 24px 0 24px;
        margin: 0;
      `;
    case 'CONTENT':
      return css`
        /* padding: 0 24px 24px 24px; */
        margin: 0;
      `;
    default:
      return css``;
  }
};
const StyledContent = styled(AContent, { shouldForwardProp: isPropValid })<IContentParams>`
  ${({ yzTypeContent }) => handleContentWithType({ yzTypeContent })}
`;
export const Content = ({ children, ...rest }: IContentParams) => (
  <StyledContent {...rest}>{children}</StyledContent>
);

// FOOTER
interface IFooterParams extends LayoutProps {}
const StyledFooter = styled(AFooter)`
  padding: 0;
  width: 100%;
`;
export const Footer = ({ children, ...rest }: IFooterParams) => (
  <StyledFooter {...rest}>{children}</StyledFooter>
);
